import React, { useEffect, useState } from 'react';
import styled from 'styled-components/native';
import Colors from '../../constants/Colors';
import {
  Modal,
  ModalProps,
  Platform,
  TouchableWithoutFeedback,
  View
} from 'react-native';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { B } from '../Typography';
import Spacer from '../Spacer';
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView
} from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming
} from 'react-native-reanimated';
import elementIDs from '../../../elementIDs';
import { scheduleOnRN } from 'react-native-worklets';

const StyledDimContainer = styled(GestureHandlerRootView)`
  width: 100%;
  height: 100%;
  justify-content: flex-end;
  background-color: rgba(0, 0, 0, 0.5);
`;

const StyledDialog = styled(Animated.View as any)`
  width: 100%;
  border-top-right-radius: 32px;
  border-top-left-radius: 32px;
  max-width: 1080px;
  overflow: hidden;
  background-color: ${Colors.background};
  padding: 8px 16px 42px 16px;
  align-self: center;
`;

const StyledWearableIconButton = styled.TouchableOpacity`
  height: 56px;
  width: 100%;
  flex-direction: row;
  margin: 0 16px 0 0;
  align-items: center;
`;

const StyledText = styled(B)`
  margin: 0 0 0 8px;
`;

const StyledIndicator = styled.View`
  height: 5px;
  width: 40px;
  background-color: #494753;
  border-radius: 10px;
  align-self: center;
  margin: 0 0 24px 0;
`;

type MeasurementsActionSheetProps = ModalProps & {
  onRefresh: () => void;
  onAddIntegration: () => void;
};

const MeasurementsActionSheet: React.FC<MeasurementsActionSheetProps> = ({
  visible,
  onRefresh,
  onAddIntegration,
  onRequestClose
}) => {
  const translateY = useSharedValue(250);
  const previousY = useSharedValue(0);
  const [height, setHeight] = useState<number>();

  const panGesture = Gesture.Pan()
    .onUpdate(e => {
      if (e.translationY < 0) {
        translateY.value = 0;
      } else {
        translateY.value = previousY.value + e.translationY;
      }
    })
    .onEnd(() => {
      if (translateY.value < (height || 0) * 0.65) {
        translateY.value = 0;
      } else if (onRequestClose) {
        scheduleOnRN(onRequestClose, undefined as any);
      }
    });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: translateY.value
        }
      ]
    };
  });

  useEffect(() => {
    translateY.value = withTiming(0);
  }, []);

  return (
    <Modal statusBarTranslucent transparent visible={visible}>
      <StyledDimContainer>
        <TouchableWithoutFeedback
          onPress={e => {
            if (onRequestClose) {
              translateY.value = withTiming(250, undefined, finished => {
                if (finished) scheduleOnRN(onRequestClose, e);
              });
            }
          }}
        >
          <View style={{ flex: 1 }} />
        </TouchableWithoutFeedback>
        <GestureDetector gesture={panGesture}>
          <StyledDialog
            onLayout={({ nativeEvent }: any) =>
              setHeight(nativeEvent.layout.height)
            }
            style={animatedStyle}
          >
            <StyledIndicator />
            <StyledWearableIconButton
              activeOpacity={0.7}
              onPress={() => onRefresh()}
            >
              <MaterialIcons name='sync' size={24} color='#615E6D' />
              <StyledText testID={elementIDs.deviceDataSyncID} color='#615E6D'>
                Synchronize Wearable Data
              </StyledText>
            </StyledWearableIconButton>
            <Spacer size={Platform.OS === 'web' ? 'large' : 'medium'} />
            <StyledWearableIconButton
              activeOpacity={0.7}
              onPress={onAddIntegration}
            >
              <Ionicons name='watch-outline' size={24} color='#615E6D' />
              <StyledText color='#615E6D'>Add Integration</StyledText>
            </StyledWearableIconButton>
          </StyledDialog>
        </GestureDetector>
      </StyledDimContainer>
    </Modal>
  );
};

export default MeasurementsActionSheet;
